// Status colours arrive from the API (status.textColor / status.bgColor) and are
// configured per company, so we cannot pick them — but we can make them legible.
//
// Measured on staging: the "Pending" pill renders textColor #6172F3 at 12px and
// scores 3.34:1, below the 4.5:1 WCAG AA needs for normal text. Re-tinting the
// background does not rescue it: the same colour over a translucent tint scores
// 3.5 on a dark card and 3.33 on a light one. The text colour itself has to move.
//
// So: keep the hue the company chose, and slide its lightness until it clears the
// threshold against the surface it is actually drawn on.

const MIN_RATIO = 4.5

const parseHex = (hex) => {
  if (typeof hex !== 'string' || hex[0] !== '#') return null
  let h = hex.slice(1)
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return null
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16))
}

const toHex = ([r, g, b]) =>
  '#' + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')

const channelLuminance = (c) => {
  const v = c / 255
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
}

const relativeLuminance = ([r, g, b]) =>
  0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b)

export const contrastRatio = (a, b) => {
  const ca = parseHex(a)
  const cb = parseHex(b)
  if (!ca || !cb) return null
  const la = relativeLuminance(ca)
  const lb = relativeLuminance(cb)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

const rgbToHsl = ([r, g, b]) => {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
  else if (max === g) h = ((b - r) / d + 2) / 6
  else h = ((r - g) / d + 4) / 6
  return [h, s, l]
}

const hslToRgb = ([h, s, l]) => {
  if (s === 0) return [l * 255, l * 255, l * 255]
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const conv = (t) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  return [conv(h + 1 / 3) * 255, conv(h) * 255, conv(h - 1 / 3) * 255]
}

// The pill's background is a translucent tint of its own text colour, so the text
// is NOT read against the card — it is read against that tint. Measuring against
// the card overstates the contrast: on stage the corrected indigo scored 4.5
// against #141B2E but only 3.78 against the tint the badge actually paints.
const TINT_ALPHA = 0.16

const composite = (rgb, surfaceRgb, alpha) =>
  rgb.map((c, i) => alpha * c + (1 - alpha) * surfaceRgb[i])

const ratioRgb = (a, b) => {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/**
 * Return `hex` if it is already legible on the pill it will be drawn in, otherwise
 * the nearest variant of the same hue that is — lightened on dark surfaces,
 * darkened on light ones. Non-hex input (CSS vars, undefined) passes through so
 * callers can hand us whatever the API gave them.
 *
 * `tinted` selects the criterion: true when the caller paints the translucent tint
 * as the background (a status pill), false when the text sits directly on the card.
 */
export const accessibleStatusColor = (hex, { dark = false, surface, tinted = true, alpha = TINT_ALPHA } = {}) => {
  const rgb = parseHex(hex)
  const surfaceRgb = parseHex(surface)
  if (!rgb || !surfaceRgb) return hex

  // `alpha` must match the tint the caller actually paints, or the criterion is
  // measured against a backdrop that never appears on screen. StatusBadge uses 0.16,
  // StatusTabs 0.12 inactive and 0.24 active.
  const backdrop = (c) => (tinted ? composite(c, surfaceRgb, alpha) : surfaceRgb)
  if (ratioRgb(rgb, backdrop(rgb)) >= MIN_RATIO) return hex

  const [h, s] = rgbToHsl(rgb)
  // On a dark card we can only get more contrast by going lighter, and vice versa.
  const step = dark ? 0.02 : -0.02
  let l = rgbToHsl(rgb)[2]

  for (let i = 0; i < 50; i++) {
    l += step
    if (l <= 0 || l >= 1) break
    const candidate = hslToRgb([h, s, l])
    if (ratioRgb(candidate, backdrop(candidate)) >= MIN_RATIO) return toHex(candidate)
  }
  // Hue could not reach the threshold at any lightness — fall back to the extreme.
  return dark ? '#FFFFFF' : '#000000'
}

// background.paper from theme/linearTheme.js — the surface a status pill sits on.
const PAPER = { dark: '#141B2E', light: '#FFFFFF' }

// Takes the mode as an argument rather than reading document.documentElement.dataset.theme.
// ThemeContext writes that attribute from a useEffect, so it lands AFTER the render that
// changed the mode has committed — a component reading it is both unsubscribed from the
// theme and a render behind. That was survivable while the tint was translucent; now that
// the pill owns an opaque background it means a dark navy pill on a white card until
// something unrelated forces a remount. Callers pass `mode` from useThemeContext().
export const surfaceForMode = (mode) => {
  const isDark = mode === 'dark'
  return { dark: isDark, surface: isDark ? PAPER.dark : PAPER.light }
}

// The tint, pre-composited over the surface into an OPAQUE colour.
//
// A translucent tint lets whatever sits behind the pill change its effective
// background. That matters because rows can carry a semantic highlight, and those
// highlights are LIGHTER than the card: measured on /accounting, the Error badge on an
// error-tinted row had a backdrop of rgb(89,46,55) instead of the assumed tint-over-card
// and fell to 3.73:1.
//
// Correcting for that with a lighter text colour does not work — to hold 4.5 on the
// lightest row the card target has to reach 6.2, which turns #F04438 into #F8A231 and
// #6172F3 into #A5AFF8, i.e. exactly the washed-out palette this design pass removed.
// Weakening the row highlight does not work either: even at alpha 0.06, where the
// highlight is all but invisible, the badge only reaches 4.11.
//
// So the pill owns its background outright. It then looks the same and measures the
// same wherever it is placed. Same reasoning the design system already applies to
// --color-bg-subtle: "solid rather than translucent so it does not stack differently
// depending on what sits behind the card".
export const opaqueStatusTint = (hex, alpha, surface) => {
  const rgb = parseHex(hex)
  const surfaceRgb = parseHex(surface)
  if (!rgb || !surfaceRgb) return null
  const c = composite(rgb, surfaceRgb, alpha).map((v) => Math.round(v))
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`
}

/**
 * Inline style for a status pill, from the raw API colours.
 *
 * The API sends an opaque pastel `bgColor` (e.g. #E0EAFF) meant for a white page.
 * Painted as-is it survives into dark mode as a pale block — which is exactly what
 * the client portal was doing. Instead the background is derived as a translucent
 * tint of the (contrast-corrected) text colour, so one value works on both themes.
 */
export const statusPillStyle = (bgColor, textColor, mode) => {
  const { dark, surface } = surfaceForMode(mode)
  const base = textColor || (dark ? '#EAEEF6' : '#18181B')
  const color = accessibleStatusColor(base, { dark, surface })
  return {
    backgroundColor: opaqueStatusTint(color, TINT_ALPHA, surface) || bgColor || (dark ? '#22293B' : '#E4E4E7'),
    color,
  }
}
